import { MagnifyingGlass, SealCheck } from "@phosphor-icons/react";
import {
  Spinner,
  compactInteger,
  parseCoverImageUrl,
  type SearchResult,
} from "@replicate/ui";
import { useState } from "react";
import { match } from "ts-pattern";
import { route } from "../../urls";
import { LogoGlyph } from "../logo-glyph";
import VideoCoverImage from "../video-cover-image";
import { useSearchMicroservice } from "./hooks";

interface SearchPrototypeProps {
  baseUrl: string;
  query?: string;
}

export default function SearchPageUI({
  baseUrl,
  query = "",
}: SearchPrototypeProps) {
  const [searchTerm, setSearchTerm] = useState<string>(query);
  const searchResults = useSearchMicroservice({
    baseUrl,
    query: searchTerm,
  });

  const isLoading = searchResults.isLoading;
  const isSuccess = searchResults.isSuccess;
  const hasModels = (searchResults.data?.results?.length ?? 0) > 0;

  const hasCollections = isSuccess
    ? searchResults.data?.metadata?.collections?.length > 0
    : false;

  return (
    <div className="space-y-4 pb-8">
      <div>
        <div className="mb-6">
          <h2>Search</h2>
        </div>

        <form
          className="max-w-3xl"
          onSubmit={(e) => {
            e.preventDefault();
          }}
        >
          <div className="relative group">
            <div className="absolute left-3 top-0 bottom-0 pointer-events-none flex items-center text-r8-gray-11">
              <MagnifyingGlass />
            </div>
            <input
              className="pl-10 pt-3 pb-3 pr-12 text-r8-lg w-full bg-r8-gray-2 focus:outline-none focus:ring"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              type="text"
              id="search-prototype"
              placeholder="Search models by name, description, or use case..."
            />
            {isLoading && searchTerm && (
              <div className="absolute right-0 top-0 bottom-0 flex items-center justify-center px-3 pointer-events-none">
                <div className="w-4 h-4">
                  <Spinner />
                </div>
              </div>
            )}
          </div>
        </form>
      </div>

      {searchTerm ? (
        <div className="flex gap-8 flex-col md:flex-row">
          <div className="space-y-8 max-w-3xl w-full">
            <section id="models" className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-r8-xl font-sans font-semibold">Models</h3>
                {searchResults.data?.results.length && (
                  <span className="text-r8-sm text-r8-gray-11">
                    {searchResults.data.results.length} result
                    {searchResults.data.results.length !== 1 ? "s" : ""}
                  </span>
                )}
              </div>

              {!hasModels && isSuccess ? (
                <div className="mb-6">
                  <p className="text-r8-gray-11 text-r8-base">
                    No models matching your query
                  </p>
                </div>
              ) : null}

              {isLoading ? (
                <p className="text-r8-gray-10 animate-pulse">
                  Loading models...
                </p>
              ) : null}

              {hasModels && (
                <div className="flex flex-col gap-6 max-w-3xl">
                  {searchResults.data?.results.map((result) => (
                    <SearchPrototypeModelCard
                      key={result.model.owner}
                      result={result}
                    />
                  ))}
                </div>
              )}
            </section>
          </div>

          {searchTerm ? (
            <div className="col-span-4 -order-1 md:order-none w-full">
              <section id="collections">
                <div className="space-y-4 max-w-sm">
                  <h3 className="text-r8-xl font-sans font-semibold relative">
                    Collections
                  </h3>
                  <div className="space-y-4 max-w-3xl relative">
                    {hasCollections &&
                      searchResults.data?.metadata.collections.map(
                        (collection) => (
                          <a
                            key={collection.slug}
                            href={route("collection_detail", {
                              slug: collection.slug,
                            })}
                            className="block border border-r8-gray-12 no-default no-hover bg-white dark:bg-r8-gray-1 hover:bg-r8-gray-2 aria-selected:bg-r8-gray-2 no-underline group p-3 md:max-w-sm w-full"
                          >
                            <p className="font-semibold">{collection.name}</p>
                            <p>{collection.description}</p>
                          </a>
                        )
                      )}
                    <a
                      href={`${route("explore")}#collections`}
                      className="block border border-r8-gray-5 focus:border-r8-gray-12 no-default no-hover bg-white dark:bg-r8-gray-1 hover:bg-r8-gray-2 aria-selected:bg-r8-gray-2 no-underline group p-3 md:max-w-sm w-full"
                    >
                      View all collections
                    </a>
                  </div>
                </div>
              </section>
            </div>
          ) : null}
        </div>
      ) : (
        <p className="text-r8-gray-11 text-r8-sm max-w-3xl">
          Need some ideas for what to search? Try{" "}
          <button
            type="button"
            onClick={() => setSearchTerm("best image models")}
          >
            <span className="text-r8-blue-11">"best image models"</span>
          </button>{" "}
          or{" "}
          <button type="button" onClick={() => setSearchTerm("text to speech")}>
            <span className="text-r8-blue-11">"text to speech"</span>
          </button>
        </p>
      )}
    </div>
  );
}

function SearchPrototypeModelCard({ result }: { result: SearchResult }) {
  // const [username, name] = result.model.owner.split("/");
  const username = result.model.owner;
  const name = result.model.name;
  const coverImage = parseCoverImageUrl({ url: result.model.cover_image_url });

  return (
    <a
      href={route("model_detail", { username, name })}
      className="block border border-r8-gray-12 no-default no-hover dark:focus:text-r8-gray-12 bg-white dark:bg-r8-gray-1 hover:bg-r8-gray-2 aria-selected:bg-r8-gray-2 no-underline group"
    >
      <div className="w-full p-3 flex flex-col h-full">
        <div className="flex items-start gap-4">
          <div className="shrink-0 w-24">
            {coverImage ? (
              <div className="isolate relative w-full h-full block">
                {match(coverImage.fileType)
                  .with("image", () => (
                    <img
                      className="object-cover h-full w-full"
                      src={coverImage.url}
                      alt={result.model.name}
                    />
                  ))
                  .with("video", () => <VideoCoverImage url={coverImage.url} />)
                  .exhaustive()}
              </div>
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <LogoGlyph />
              </div>
            )}
          </div>
          <div className="flex flex-col">
            <div className="overflow-ellipsis flex-1 flex-shrink-0 text-r8-lg leading-tight">
              <span className="text-r8-gray-11 group-focus:text-r8-gray-1">
                {username}
              </span>
              <span className="text-r8-gray-11">/</span>
              <span className="font-semibold">{name}</span>
              {/* OFFICIAL_MODEL_AUDIT(aron): Used to display official model seal */}
              {result.metadata.is_official && (
                <SealCheck
                  weight="fill"
                  className="inline relative -top-px ml-1 text-r8-gray-12"
                />
              )}
            </div>

            <div className="flex-1 my-2">
              {result.model.description ? (
                <p className="text-r8-base line-clamp-3 overflow-ellipsis text-r8-gray-12 group-focus:text-r8-gray-1">
                  {result.model.description}
                </p>
              ) : (
                <p className="text-r8-sm text-r8-gray-11 group-focus:text-r8-gray-1">
                  No description provided.
                </p>
              )}
            </div>

            {result.metadata.tags.length > 0 && (
              <div>
                <div className="flex flex-wrap gap-1">
                  {result.metadata.tags.slice(0, 5).map((tag) => (
                    <span
                      key={tag}
                      className="px-1.5 py-0.5 text-xs bg-r8-gray-3 text-r8-gray-11 group-focus:bg-r8-gray-12 border border-r8-gray-3 group-focus:border-r8-gray-11 group-focus:text-r8-gray-5"
                    >
                      {tag}
                    </span>
                  ))}
                  {result.metadata.tags.length > 5 && (
                    <span className="px-2 py-1 text-xs text-r8-gray-11 group-focus:text-r8-gray-5">
                      +{result.metadata.tags.length - 5} more
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="flex-1 mt-4 flex flex-col lg:flex-row lg:items-start border-t border-r8-gray-6 divide-y lg:divide-y-0 lg:divide-x">
          <div className="flex flex-col py-2 lg:pb-0 lg:pr-4">
            <span className="text-r8-gray-11 group-focus:text-r8-gray-1 text-r8-sm">
              Runs
            </span>
            <span className="text-r8-sm text-r8-red-11">
              {compactInteger(result.model.run_count)}
            </span>
          </div>

          {result.metadata.last_updated ? (
            <div className="flex flex-col py-2 lg:pb-0 lg:px-4">
              <span className="text-r8-gray-11 group-focus:text-r8-gray-1 text-r8-sm">
                Last updated
              </span>
              <span className="text-r8-sm">
                {new Date(result.metadata.last_updated).toLocaleDateString()}
              </span>
            </div>
          ) : null}
        </div>
      </div>
    </a>
  );
}
